import { useCallback, useEffect, useMemo, useState } from 'react'
import { Coins, Loader2 } from 'lucide-react'

import { SalonReportFilterBar } from '@/components/reports/SalonReportFilterBar'
import { SalonDataTable } from '@/components/ui/SalonDataTable'
import { useBranch } from '@/contexts/BranchContext'
import { fetchStylists } from '@/lib/api/stylists'
import {
  exportStaffCommissionReportCsv,
  fetchStaffCommissionReport,
} from '@/lib/api/staffCommissions'
import { formatReportMoney } from '@/lib/api/reports'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { resolveReportPeriodBounds } from '@/lib/reports/filters'
import { salonReportPeriodLabel } from '@/lib/reports/salonReportFilters'
import { formatSaleHistoryDate } from '@/lib/sales/saleHistoryDisplay'

export function StaffCommissionPanel() {
  const { activeBranchId } = useBranch()
  const [period, setPeriod] = useState('month')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [stylistId, setStylistId] = useState('')
  const [stylists, setStylists] = useState([])
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState('')

  const bounds = useMemo(() => {
    try {
      return resolveReportPeriodBounds(period, { from: customFrom, to: customTo })
    } catch {
      return null
    }
  }, [period, customFrom, customTo])

  const loadReport = useCallback(async () => {
    if (period === 'custom' && (!customFrom || !customTo)) {
      setReport(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError('')

    try {
      const data = await fetchStaffCommissionReport({
        period,
        customFrom,
        customTo,
        stylistId: stylistId === '' ? null : Number(stylistId),
      })
      setReport(data)
    } catch (err) {
      setReport(null)
      setError(apiErrorMessage(err, 'Could not load staff commission report.'))
    } finally {
      setLoading(false)
    }
  }, [period, customFrom, customTo, stylistId])

  useEffect(() => {
    let active = true

    fetchStylists()
      .then((items) => {
        if (active) setStylists(items.filter((item) => item.is_active))
      })
      .catch(() => {
        if (active) setStylists([])
      })

    return () => {
      active = false
    }
  }, [activeBranchId])

  useEffect(() => {
    loadReport()
  }, [loadReport, activeBranchId])

  async function handleExport() {
    setExporting(true)
    setError('')
    try {
      await exportStaffCommissionReportCsv({
        period,
        customFrom,
        customTo,
        stylistId: stylistId === '' ? null : Number(stylistId),
      })
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not export staff commission report.'))
    } finally {
      setExporting(false)
    }
  }

  const summary = report?.summary ?? {}
  const byStylist = report?.by_stylist ?? []
  const lines = report?.lines ?? []
  const periodLabel = salonReportPeriodLabel(period, bounds ?? {})

  const byStylistColumns = [
    {
      key: 'stylist_name',
      label: 'Staff',
      render: (row) => <span className="font-medium">{row.stylist_name}</span>,
    },
    {
      key: 'line_count',
      label: 'Services',
      className: 'tabular-nums',
      render: (row) => row.line_count,
    },
    {
      key: 'sales_total',
      label: 'Sales (AED)',
      className: 'text-right tabular-nums',
      headerClassName: 'text-right',
      render: (row) => formatReportMoney(row.sales_total),
    },
    {
      key: 'commission_total',
      label: 'Commission (AED)',
      className: 'text-right tabular-nums font-semibold text-primary',
      headerClassName: 'text-right',
      render: (row) => formatReportMoney(row.commission_total),
    },
  ]

  const lineColumns = [
    {
      key: 'completed_at',
      label: 'Date',
      render: (row) => formatSaleHistoryDate(row.completed_at),
    },
    {
      key: 'sale_number',
      label: 'Receipt',
      render: (row) => <span className="font-medium">#{row.sale_number}</span>,
    },
    {
      key: 'stylist_name',
      label: 'Staff',
      render: (row) => row.stylist_name,
    },
    {
      key: 'service_name',
      label: 'Service',
      render: (row) => row.service_name,
    },
    {
      key: 'line_total',
      label: 'Sale (AED)',
      className: 'text-right tabular-nums',
      headerClassName: 'text-right',
      render: (row) => formatReportMoney(row.line_total),
    },
    {
      key: 'commission_percent',
      label: 'Rate',
      className: 'text-right tabular-nums',
      headerClassName: 'text-right',
      render: (row) => `${row.commission_percent}%`,
    },
    {
      key: 'commission_amount',
      label: 'Commission (AED)',
      className: 'text-right tabular-nums font-semibold',
      headerClassName: 'text-right',
      render: (row) => formatReportMoney(row.commission_amount),
    },
  ]

  return (
    <div className="mt-6 space-y-6">
      <SalonReportFilterBar
        period={period}
        customFrom={customFrom}
        customTo={customTo}
        onPeriodChange={setPeriod}
        onCustomFromChange={setCustomFrom}
        onCustomToChange={setCustomTo}
        onExportCsv={handleExport}
        exporting={exporting}
        loading={loading}
      />

      <div className="salon-panel p-4">
        <label className="block max-w-sm space-y-1.5">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Staff member
          </span>
          <select
            value={stylistId}
            onChange={(e) => setStylistId(e.target.value)}
            className="h-11 w-full rounded-xl border border-border/80 bg-background px-3 text-sm"
          >
            <option value="">All staff</option>
            {stylists.map((stylist) => (
              <option key={stylist.id} value={stylist.id}>
                {stylist.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error ? (
        <p className="rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-900" role="alert">
          {error}
        </p>
      ) : null}

      {loading ? (
        <div className="flex items-center gap-2 py-12 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
          <span>Loading commission report…</span>
        </div>
      ) : (
        <>
          <section>
            <div className="mb-3 flex items-center gap-2 text-primary">
              <Coins className="h-5 w-5" aria-hidden />
              <h2 className="text-lg font-semibold">{periodLabel}</h2>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="salon-stat-card">
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Services
                </p>
                <p className="mt-2 text-2xl font-bold tabular-nums">{summary.line_count ?? 0}</p>
              </div>
              <div className="salon-stat-card">
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Sales total
                </p>
                <p className="mt-2 text-2xl font-bold tabular-nums">
                  AED {formatReportMoney(summary.sales_total)}
                </p>
              </div>
              <div className="salon-stat-card">
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Commission total
                </p>
                <p className="mt-2 text-2xl font-bold tabular-nums text-primary">
                  AED {formatReportMoney(summary.commission_total)}
                </p>
              </div>
            </div>
          </section>

          <SalonDataTable
            title="By staff member"
            columns={byStylistColumns}
            rows={byStylist}
            loading={false}
            getRowKey={(row) => row.stylist_id ?? row.stylist_name}
            emptyLabel="No commission in this period. Set commission % on services and assign staff at checkout."
          />

          <SalonDataTable
            title="Line detail"
            columns={lineColumns}
            rows={lines}
            loading={false}
            getRowKey={(row) => row.id}
            emptyLabel="No lines to show."
          />
        </>
      )}
    </div>
  )
}
